import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from './operations';

const TOKEN_KEY = 'eco-auth-token';
const USER_KEY = 'eco-auth-user';

export const getAuthUser = () => {
  try {
    return JSON.parse(localStorage.getItem(USER_KEY) || 'null');
  } catch {
    return null;
  }
};

export const setAuth = (token, user) => {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
};

export const clearAuth = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};

const sanitizeNext = (path) => {
  // never redirect back into /login (breaks the post-login navigation)
  if (!path || path.startsWith('/login')) return null;
  return path;
};

/**
 * Route guard hook for dashboard pages.
 * Verifies the stored token against /auth/me and enforces an optional role.
 * Returns { user, ready } — render nothing (or a loader) until ready.
 */
export const useRequireAuth = (requiredRole = null) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);
  // capture the intended destination once, before any redirect (StrictMode runs effects twice)
  const targetRef = useRef(sanitizeNext(`${window.location.pathname}${window.location.search}`));

  useEffect(() => {
    const target = targetRef.current;
    if (!target) {
      navigate('/', { replace: true });
      return;
    }
    if (!localStorage.getItem(TOKEN_KEY)) {
      navigate(`/login?next=${encodeURIComponent(target)}`, { replace: true });
      return;
    }
    apiFetch('/auth/me')
      .then((u) => {
        if (requiredRole && u.role !== requiredRole && u.role !== 'admin') {
          // valid session, wrong area: keep the session and send the user to their own dashboard
          navigate(u.role === 'partner' ? '/partner' : '/', { replace: true });
          return;
        }
        setAuth(localStorage.getItem(TOKEN_KEY), u);
        setUser(u);
        setReady(true);
      })
      .catch((err) => {
        if (err?.status === 401 || err?.status === 403) {
          clearAuth();
          navigate(`/login?next=${encodeURIComponent(target)}`, { replace: true });
          return;
        }
        // transient failure (reload race/network): trust the cached session and revalidate on next load
        const cached = getAuthUser();
        if (cached && (!requiredRole || cached.role === requiredRole || cached.role === 'admin')) {
          setUser(cached);
          setReady(true);
          return;
        }
        navigate(`/login?next=${encodeURIComponent(target)}`, { replace: true });
      });
    // mount-only guard
  }, []);

  return { user, ready };
};

export const logout = async (navigate) => {
  try {
    await apiFetch('/auth/logout', { method: 'POST' });
  } catch {
    /* token discard is client-side */
  }
  clearAuth();
  navigate('/');
};
